import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import QRCode from 'qrcode'
import {
  ArrowDownToLine, ArrowUpFromLine, AlertTriangle, ExternalLink, Loader2, Check,
  ShieldCheck, Search, Plus, Trash2, Info, RefreshCw, Wallet,
} from 'lucide-react'
import { PanelShell } from '../components/Layout'
import { Card, Tabs, Button, Field, Empty, Rows, CopyButton, Badge, NumberInput, Modal } from '../components/UI'
import { CoinIcon } from '../components/Market'
import { useAuth, toast } from '../store'
import { get, post, del } from '../lib/api'
import { clsx, num, fa, faDate, shortAddr, toEnDigits, asArray} from '../lib/format'

type NetworkInfo = {
  key: string; faName: string; family: string; kind: string; contract: string
  decimals: number; minDeposit: number; minWithdraw: number; withdrawFee: number
  confirmations: number; native: string
}
type AssetInfo = { symbol: string; faName: string; color: string; networks: NetworkInfo[] }

/* ---------------------------------------------------------------- QR */
function Qr({ text, size = 168 }: { text: string; size?: number }) {
  const [url, setUrl] = useState('')
  useEffect(() => {
    if (!text) return setUrl('')
    QRCode.toDataURL(text, {
      width: size * 2, margin: 1,
      color: { dark: '#05070F', light: '#FFFFFF' },
      errorCorrectionLevel: 'M',
    }).then(setUrl).catch(() => setUrl(''))
  }, [text, size])
  if (!url) {
    return <div className="skeleton rounded-xl" style={{ width: size, height: size }} />
  }
  return (
    <img src={url} width={size} height={size} alt="QR"
      className="rounded-xl border-4 border-white bg-white" />
  )
}

/* ---------------------------------------------------------------- انتخاب ارز/شبکه */
function AssetPicker({
  assets, symbol, network, onChange,
}: {
  assets: AssetInfo[]
  symbol: string
  network: string
  onChange: (s: string, n: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const current = assets.find((a) => a.symbol === symbol)
  const nets = current?.networks ?? []
  const rows = assets.filter((a) => !q || a.symbol.toLowerCase().includes(q.toLowerCase()) || a.faName.includes(q))

  return (
    <>
      <Field label="ارز">
        <button onClick={() => setOpen(true)}
          className="flex w-full items-center gap-3 rounded-xl border border-white/10 bg-ink-900/70 px-4 py-3 text-right transition hover:border-brand-500/40">
          {current ? <CoinIcon symbol={current.symbol} color={current.color} size={30} /> : null}
          <div className="flex-1">
            <div className="text-sm font-semibold text-white">{current?.faName ?? '—'}</div>
            <div className="num text-[11px] text-slate-500" dir="ltr">{symbol}</div>
          </div>
          <span className="text-xs text-slate-500">تغییر</span>
        </button>
      </Field>

      <Field label="شبکه انتقال" hint="شبکه را دقیقاً مطابق کیف پول مبدأ/مقصد انتخاب کنید">
        <div className="flex flex-wrap gap-2">
          {nets.map((n) => (
            <button key={n.key} onClick={() => onChange(symbol, n.key)}
              className={clsx('chip transition',
                network === n.key ? 'border-brand-500/50 bg-brand-500/15 text-brand-200' : 'hover:border-white/25')}>
              {n.faName}
              {n.kind === 'token' && <span className="text-[10px] text-slate-500">توکن</span>}
            </button>
          ))}
          {nets.length === 0 && <span className="text-xs text-slate-500">شبکه فعالی برای این ارز وجود ندارد</span>}
        </div>
      </Field>

      <Modal open={open} onClose={() => setOpen(false)} title="انتخاب ارز">
        <div className="relative mb-3">
          <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input className="input pr-10" placeholder="جستجو" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="max-h-80 space-y-1 overflow-y-auto">
          {rows.map((a) => (
            <button key={a.symbol}
              onClick={() => { onChange(a.symbol, a.networks[0]?.key ?? ''); setOpen(false); setQ('') }}
              className="flex w-full items-center gap-3 rounded-xl p-2.5 text-right transition hover:bg-white/[.06]">
              <CoinIcon symbol={a.symbol} color={a.color} size={32} />
              <div className="flex-1">
                <div className="text-sm text-white">{a.faName}</div>
                <div className="num text-[11px] text-slate-500" dir="ltr">{a.symbol}</div>
              </div>
              <div className="text-[11px] text-slate-500">{a.networks.map((n) => n.key).join('، ')}</div>
            </button>
          ))}
        </div>
      </Modal>
    </>
  )
}

/* ---------------------------------------------------------------- واریز */
function DepositPane({ assets }: { assets: AssetInfo[] }) {
  const [symbol, setSymbol] = useState('USDT')
  const [network, setNetwork] = useState('')
  const [info, setInfo] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [rows, setRows] = useState<any[] | null>(null)

  useEffect(() => {
    if (!assets.length) return
    const a = assets.find((x) => x.symbol === symbol) ?? assets[0]
    setSymbol(a.symbol)
    if (!network || !a.networks.some((n) => n.key === network)) setNetwork(a.networks[0]?.key ?? '')
  }, [assets])

  useEffect(() => {
    setInfo(null); setError('')
    if (!symbol || !network) return
    setLoading(true)
    // اگر کاربر سریع ارز/شبکه را عوض کند، ممکن است پاسخ درخواست قبلی دیرتر برسد.
    // بدون این نگهبان، آدرسِ ارز قبلی زیر نام ارز جدید نمایش داده می‌شود —
    // و ارسال دارایی به چنین آدرسی یعنی از دست رفتن همیشگی آن.
    let stale = false
    get(`/chain/deposit-address?symbol=${symbol}&network=${network}`)
      .then((r: any) => { if (!stale) setInfo(r) })
      .catch((e) => { if (!stale) setError(e.message) })
      .finally(() => { if (!stale) setLoading(false) })
    return () => { stale = true }
  }, [symbol, network])

  // آدرس فقط وقتی نمایش داده می‌شود که دقیقاً متعلق به همین ارز و همین شبکه باشد
  const shown = info && info.symbol === symbol && info.network === network ? info : null

  const loadRows = () => get('/chain/deposits').then((r: any) => setRows(asArray(r))).catch(() => setRows([]))
  useEffect(() => {
    loadRows()
    const id = setInterval(loadRows, 20000)
    return () => clearInterval(id)
  }, [])

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_1.1fr]">
      <Card>
        <AssetPicker assets={assets} symbol={symbol} network={network}
          onChange={(s, n) => { setSymbol(s); setNetwork(n) }} />

        {loading && (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" /> در حال ساخت آدرس اختصاصی…
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-down/25 bg-down/[.07] p-3 text-xs leading-6 text-down">
            {error}
          </div>
        )}

        {shown && (
          <>
            <div className="mb-4 flex flex-col items-center gap-4 rounded-2xl border border-white/[.07] bg-ink-900/60 p-5">
              {/* برچسب ارز و شبکه دقیقاً کنار آدرس تکرار می‌شود تا هیچ ابهامی نماند */}
              <div className="flex flex-wrap items-center justify-center gap-2">
                <span className="rounded-full border border-brand-500/30 bg-brand-500/10 px-3 py-1 text-xs font-bold text-brand-200">
                  {shown.symbol}
                </span>
                <span className="rounded-full border border-white/10 bg-white/[.04] px-3 py-1 text-xs text-slate-300">
                  شبکه {shown.networkName}
                </span>
              </div>
              <Qr text={shown.address} />
              <div className="w-full">
                <div className="mb-1.5 text-center text-[11px] text-slate-500">
                  آدرس واریز اختصاصی شما برای {shown.symbol} روی {shown.networkName}
                </div>
                <div className="num break-all rounded-xl border border-white/[.07] bg-ink-950/70 px-3 py-2.5 text-center text-xs text-slate-200" dir="ltr">
                  {shown.address}
                </div>
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                <CopyButton text={shown.address} label="کپی آدرس" />
                {shown.explorer && (
                  <a href={shown.explorer} target="_blank" rel="noreferrer" className="btn-ghost !px-3 !py-2 text-xs">
                    <ExternalLink className="h-4 w-4" /> مشاهده در اکسپلورر
                  </a>
                )}
              </div>
              {shown.memo && (
                <div className="w-full rounded-xl border border-gold/25 bg-gold/[.07] p-3 text-center text-xs text-gold">
                  ممو / تگ الزامی: <span className="num font-bold">{shown.memo}</span>
                </div>
              )}
            </div>

            <div className="mb-4 grid grid-cols-2 gap-2 text-xs">
              <div className="rounded-xl border border-white/[.06] bg-white/[.02] p-3">
                <div className="mb-1 text-[10px] text-slate-500">حداقل واریز</div>
                <div className="num text-slate-200">{num(shown.minDeposit, 8)} {shown.symbol}</div>
              </div>
              <div className="rounded-xl border border-white/[.06] bg-white/[.02] p-3">
                <div className="mb-1 text-[10px] text-slate-500">تأیید لازم شبکه</div>
                <div className="num text-slate-200">{fa(shown.confirmations)} بلاک</div>
              </div>
            </div>

            <div className="flex items-start gap-2 rounded-xl border border-down/25 bg-down/[.06] p-3 text-[11px] leading-6 text-down">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{shown.warning}</span>
            </div>

            {!shown.isMainnet && (
              <div className="mt-3 flex items-start gap-2 rounded-xl border border-brand-500/25 bg-brand-500/[.07] p-3 text-[11px] leading-6 text-brand-200">
                <Info className="mt-0.5 h-4 w-4 shrink-0" />
                این آدرس روی <b>شبکه آزمایشی</b> است؛ فقط دارایی تست‌نت به آن ارسال کنید.
              </div>
            )}
          </>
        )}
      </Card>

      <Card className="!p-0">
        <div className="flex items-center justify-between border-b border-white/[.06] p-4">
          <h3 className="font-bold text-white">واریزهای اخیر</h3>
          <button onClick={loadRows} className="btn-ghost !px-2.5 !py-1.5 text-xs">
            <RefreshCw className="h-3.5 w-3.5" /> بروزرسانی
          </button>
        </div>
        <div className="p-2">
          {rows === null ? <div className="p-3"><Rows n={4} /></div>
            : rows.length === 0 ? <Empty text="هنوز واریز زنجیره‌ای ثبت نشده است" icon={<ArrowDownToLine className="h-6 w-6" />} />
            : rows.map((d) => (
              <div key={d.id} className="border-b border-white/[.05] p-3 last:border-0">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="num text-sm font-bold text-white">{num(d.amount, 8)} {d.symbol}</span>
                  <Badge kind="muted">{d.networkName}</Badge>
                  {d.status === 'credited' ? <Badge kind="up"><Check className="h-3 w-3" /> واریز شد</Badge>
                    : d.status === 'ignored' ? <Badge kind="down">نادیده گرفته شد</Badge>
                    : <Badge kind="gold">در انتظار تأیید</Badge>}
                  <span className="mr-auto text-[11px] text-slate-500">{faDate(d.createdAt)}</span>
                </div>
                {d.status === 'pending' && (
                  <>
                    <div className="mb-1 h-1.5 overflow-hidden rounded-full bg-white/[.06]">
                      <div className="h-full rounded-full bg-gradient-to-l from-brand-600 to-aqua transition-all"
                        style={{ width: `${Math.min((d.confirmations / Math.max(d.required, 1)) * 100, 100)}%` }} />
                    </div>
                    <div className="num text-[10px] text-slate-500">
                      {fa(d.confirmations)} از {fa(d.required)} تأیید
                    </div>
                  </>
                )}
                {d.note && <div className="mt-1 text-[11px] text-slate-500">{d.note}</div>}
                {d.explorer && (
                  <a href={d.explorer} target="_blank" rel="noreferrer"
                    className="num mt-1.5 inline-flex items-center gap-1 text-[11px] text-brand-300 hover:underline" dir="ltr">
                    {shortAddr(d.txid, 10)} <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            ))}
        </div>
      </Card>
    </div>
  )
}

/* ---------------------------------------------------------------- برداشت */
function WithdrawPane({ assets }: { assets: AssetInfo[] }) {
  const { user } = useAuth()
  const [symbol, setSymbol] = useState('USDT')
  const [network, setNetwork] = useState('')
  const [address, setAddress] = useState('')
  const [amount, setAmount] = useState('')
  const [otp, setOtp] = useState('')
  const [save, setSave] = useState(false)
  const [valid, setValid] = useState<boolean | null>(null)
  const [quote, setQuote] = useState<any>(null)
  const [balances, setBalances] = useState<Record<string, number>>({})
  const [book, setBook] = useState<any[]>([])
  const [rows, setRows] = useState<any[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [confirm, setConfirm] = useState(false)

  const spec = useMemo(
    () => assets.find((a) => a.symbol === symbol)?.networks.find((n) => n.key === network),
    [assets, symbol, network],
  )

  useEffect(() => {
    if (!assets.length) return
    const a = assets.find((x) => x.symbol === symbol) ?? assets[0]
    setSymbol(a.symbol)
    if (!network || !a.networks.some((n) => n.key === network)) setNetwork(a.networks[0]?.key ?? '')
  }, [assets])

  const loadAll = () => {
    get('/wallet/balances').then((r: any) => {
      const m: Record<string, number> = {}
      r.assets.forEach((a: any) => (m[a.currency] = a.balance))
      setBalances(m)
    }).catch(() => {})
    get('/chain/address-book').then((r: any) => setBook(asArray(r))).catch(() => {})
    get('/chain/withdrawals').then((r: any) => setRows(asArray(r))).catch(() => setRows([]))
  }
  useEffect(() => {
    loadAll()
    const id = setInterval(() => get('/chain/withdrawals').then((r: any) => setRows(asArray(r))).catch(() => {}), 15000)
    return () => clearInterval(id)
  }, [])

  // نگهبان stale جلوی نشستن پاسخِ دیررسِ شبکه قبلی روی انتخاب فعلی را می‌گیرد
  useEffect(() => {
    setValid(null)
    if (!address || !network) return
    let stale = false
    const id = setTimeout(() => {
      get(`/chain/validate?network=${network}&address=${encodeURIComponent(address.trim())}`)
        .then((r: any) => { if (!stale) setValid(r.valid) })
        .catch(() => { if (!stale) setValid(null) })
    }, 350)
    return () => { stale = true; clearTimeout(id) }
  }, [address, network])

  useEffect(() => {
    setQuote(null)
    const a = Number(amount)
    if (!a || !symbol || !network) return
    let stale = false
    const id = setTimeout(() => {
      get(`/chain/withdraw-quote?symbol=${symbol}&network=${network}&amount=${a}`)
        .then((r: any) => { if (!stale) setQuote(r) })
        .catch(() => { if (!stale) setQuote(null) })
    }, 250)
    return () => { stale = true; clearTimeout(id) }
  }, [amount, symbol, network])

  const available = balances[symbol] ?? 0

  const submit = async () => {
    setLoading(true)
    try {
      await post('/chain/withdraw', {
        symbol, network, address: address.trim(), amount: Number(amount),
        otp, save_address: save, label: 'آدرس ذخیره‌شده',
      })
      toast.ok('درخواست برداشت ثبت شد و در حال ارسال به شبکه است')
      setAmount(''); setOtp(''); setConfirm(false)
      loadAll()
    } catch (e: any) {
      toast.err(e.message)
    } finally { setLoading(false) }
  }

  const canSubmit = valid === true && Number(amount) > 0 && quote?.receive > 0 && Number(amount) <= available

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_1.1fr]">
      <Card>
        <AssetPicker assets={assets} symbol={symbol} network={network}
          onChange={(s, n) => { setSymbol(s); setNetwork(n) }} />

        <Field label="آدرس مقصد"
          error={valid === false ? 'این آدرس برای شبکه انتخاب‌شده معتبر نیست' : ''}
          hint={valid === true ? '✓ آدرس معتبر است' : 'آدرس را از کیف پول مقصد کپی کنید'}>
          <div className="relative">
            <input className="input num pl-9" dir="ltr" value={address}
              onChange={(e) => setAddress(e.target.value)} placeholder="آدرس کیف پول مقصد" />
            {valid === true && <Check className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-up" />}
            {valid === false && <AlertTriangle className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-down" />}
          </div>
        </Field>

        {book.filter((b) => b.network === network).length > 0 && (
          <div className="mb-4 flex flex-wrap gap-2">
            {book.filter((b) => b.network === network).map((b) => (
              <button key={b.id} onClick={() => setAddress(b.address)}
                className="chip text-[11px] hover:border-brand-500/40 hover:text-brand-200">
                {b.label} — <span className="num" dir="ltr">{shortAddr(b.address, 6)}</span>
              </button>
            ))}
          </div>
        )}

        <Field label="مقدار برداشت"
          hint={`موجودی قابل برداشت: ${num(available, 8)} ${symbol}`}>
          <NumberInput value={amount} onChange={setAmount} suffix={symbol} />
        </Field>
        <div className="mb-4 grid grid-cols-4 gap-1.5">
          {[25, 50, 75, 100].map((p) => (
            <button key={p} onClick={() => setAmount(String(Number((available * p / 100).toFixed(8))))}
              className="rounded-lg border border-white/[.07] bg-white/[.03] py-1.5 text-[11px] text-slate-400 transition hover:border-brand-500/40 hover:text-brand-200">
              {fa(p)}٪
            </button>
          ))}
        </div>

        {user?.twoFa && (
          <Field label="کد دومرحله‌ای">
            <input className="input num tracking-[.4em]" dir="ltr" maxLength={6} value={otp}
              onChange={(e) => setOtp(toEnDigits(e.target.value).replace(/\D/g, ''))} />
          </Field>
        )}

        <label className="mb-4 flex cursor-pointer items-center gap-2 text-xs text-slate-400">
          <input type="checkbox" className="h-4 w-4 accent-brand-500" checked={save}
            onChange={(e) => setSave(e.target.checked)} />
          این آدرس در دفترچه آدرس‌های من ذخیره شود
        </label>

        {spec && (
          <div className="mb-4 space-y-2 rounded-2xl border border-white/[.06] bg-white/[.02] p-4 text-xs">
            <div className="flex justify-between"><span className="text-slate-500">حداقل برداشت</span>
              <span className="num text-slate-200">{num(spec.minWithdraw, 8)} {symbol}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">کارمزد شبکه</span>
              <span className="num text-slate-200">{num(spec.withdrawFee, 8)} {symbol}</span></div>
            <div className="flex justify-between border-t border-white/[.06] pt-2">
              <span className="text-slate-400">مبلغ دریافتی مقصد</span>
              <span className="num font-bold text-up">{num(quote?.receive ?? 0, 8)} {symbol}</span></div>
          </div>
        )}

        <Button className="w-full !py-3.5" disabled={!canSubmit} onClick={() => setConfirm(true)}>
          <ArrowUpFromLine className="h-4 w-4" /> ثبت درخواست برداشت
        </Button>

        {(user?.kycLevel ?? 0) < 2 && (
          <p className="mt-3 text-center text-[11px] text-gold">
            برای برداشت رمزارز باید <Link to="/kyc" className="underline">احراز هویت سطح ۲</Link> را کامل کنید.
          </p>
        )}
      </Card>

      <Card className="!p-0">
        <div className="border-b border-white/[.06] p-4"><h3 className="font-bold text-white">برداشت‌های اخیر</h3></div>
        <div className="p-2">
          {rows === null ? <div className="p-3"><Rows n={4} /></div>
            : rows.length === 0 ? <Empty text="برداشت زنجیره‌ای ثبت نشده است" icon={<ArrowUpFromLine className="h-6 w-6" />} />
            : rows.map((w) => (
              <div key={w.id} className="border-b border-white/[.05] p-3 last:border-0">
                <div className="mb-1.5 flex flex-wrap items-center gap-2">
                  <span className="num text-sm font-bold text-white">{num(w.amount, 8)} {w.symbol}</span>
                  <Badge kind="muted">{w.networkName}</Badge>
                  {w.status === 'confirmed' ? <Badge kind="up"><Check className="h-3 w-3" /> انجام شد</Badge>
                    : w.status === 'sent' ? <Badge kind="info">ارسال شد — در انتظار تأیید</Badge>
                    : w.status === 'sending' ? <Badge kind="gold">در حال ارسال</Badge>
                    : w.status === 'review' ? <Badge kind="gold">در انتظار بررسی</Badge>
                    : w.status === 'failed' ? <Badge kind="down">ناموفق</Badge>
                    : w.status === 'rejected' ? <Badge kind="down">رد شد</Badge>
                    : <Badge kind="muted">در صف</Badge>}
                  <span className="mr-auto text-[11px] text-slate-500">{faDate(w.createdAt)}</span>
                </div>
                <div className="num text-[11px] text-slate-500" dir="ltr">→ {shortAddr(w.toAddress, 12)}</div>
                {w.explorer && (
                  <a href={w.explorer} target="_blank" rel="noreferrer"
                    className="num mt-1 inline-flex items-center gap-1 text-[11px] text-brand-300 hover:underline" dir="ltr">
                    {shortAddr(w.txid, 10)} <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                {w.error && <div className="mt-1 text-[11px] text-down">{w.error}</div>}
              </div>
            ))}
        </div>
      </Card>

      <Modal open={confirm} onClose={() => setConfirm(false)} title="تأیید برداشت رمزارز">
        <div className="mb-4 flex items-start gap-2 rounded-xl border border-down/25 bg-down/[.06] p-3 text-[11px] leading-6 text-down">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          تراکنش‌های بلاک‌چین برگشت‌ناپذیرند. آدرس و شبکه را با دقت بررسی کنید.
        </div>
        <div className="mb-4 space-y-2 rounded-2xl border border-white/[.07] bg-ink-900/60 p-4 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">ارز و شبکه</span>
            <span className="text-white">{symbol} — {spec?.faName}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">مبلغ کسرشده</span>
            <span className="num text-white">{num(Number(amount), 8)} {symbol}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">کارمزد</span>
            <span className="num text-slate-300">{num(spec?.withdrawFee ?? 0, 8)} {symbol}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">دریافتی مقصد</span>
            <span className="num font-bold text-up">{num(quote?.receive ?? 0, 8)} {symbol}</span></div>
          <div className="border-t border-white/[.06] pt-2">
            <div className="mb-1 text-slate-500">آدرس مقصد</div>
            <div className="num break-all text-xs text-slate-200" dir="ltr">{address}</div>
          </div>
        </div>
        <Button loading={loading} onClick={submit} className="w-full !py-3">
          <ShieldCheck className="h-4 w-4" /> تأیید نهایی و ارسال
        </Button>
      </Modal>
    </div>
  )
}

/* ---------------------------------------------------------------- دفترچه آدرس */
function AddressBookPane() {
  const [rows, setRows] = useState<any[] | null>(null)
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({ network: 'TRC20', address: '', label: '', symbol: '' })
  const [nets, setNets] = useState<string[]>([])
  const [loading, setLoading] = useState(false)

  const load = () => get('/chain/address-book').then((r: any) => setRows(asArray(r))).catch(() => setRows([]))
  useEffect(() => {
    load()
    get('/chain/status').then((s: any) =>
      setNets((s.networks || []).filter((n: any) => n.active).map((n: any) => n.key))).catch(() => {})
  }, [])

  const add = async () => {
    setLoading(true)
    try {
      await post('/chain/address-book', form)
      toast.ok('آدرس ذخیره شد'); setOpen(false); setForm({ ...form, address: '', label: '' }); load()
    } catch (e: any) { toast.err(e.message) } finally { setLoading(false) }
  }

  return (
    <Card className="!p-0">
      <div className="flex items-center justify-between border-b border-white/[.06] p-4">
        <h3 className="font-bold text-white">دفترچه آدرس‌های برداشت</h3>
        <Button variant="ghost" className="!py-2 text-xs" onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4" /> افزودن آدرس
        </Button>
      </div>
      <div className="p-2">
        {rows === null ? <div className="p-3"><Rows n={3} /></div>
          : rows.length === 0 ? <Empty text="آدرسی ذخیره نشده است" icon={<Wallet className="h-6 w-6" />} />
          : rows.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-3 border-b border-white/[.05] p-3 last:border-0">
              <Badge kind="info">{r.network}</Badge>
              <div className="min-w-0">
                <div className="text-sm text-white">{r.label}</div>
                <div className="num truncate text-[11px] text-slate-500" dir="ltr">{r.address}</div>
              </div>
              <div className="mr-auto flex items-center gap-2">
                <CopyButton text={r.address} label="" />
                <button onClick={async () => { await del(`/chain/address-book/${r.id}`); toast.ok('حذف شد'); load() }}
                  className="rounded-lg p-2 text-slate-500 transition hover:bg-down/10 hover:text-down">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="افزودن آدرس برداشت">
        <Field label="شبکه">
          <select className="input" value={form.network} onChange={(e) => setForm({ ...form, network: e.target.value })}>
            {nets.map((n) => <option key={n} value={n} className="bg-ink-900">{n}</option>)}
          </select>
        </Field>
        <Field label="آدرس">
          <input className="input num" dir="ltr" value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </Field>
        <Field label="عنوان">
          <input className="input" value={form.label} placeholder="مثلاً کیف پول شخصی"
            onChange={(e) => setForm({ ...form, label: e.target.value })} />
        </Field>
        <Button loading={loading} onClick={add} className="w-full !py-3">ذخیره آدرس</Button>
      </Modal>
    </Card>
  )
}

/* ---------------------------------------------------------------- صفحه */
export default function Crypto() {
  const { user } = useAuth()
  const [tab, setTab] = useState('deposit')
  const [assets, setAssets] = useState<AssetInfo[]>([])
  const [status, setStatus] = useState<any>(null)

  useEffect(() => {
    get('/chain/assets').then((r: any) => setAssets(asArray(r))).catch(() => setAssets([]))
    get('/chain/status').then(setStatus).catch(() => {})
  }, [])

  if (status && !status.enabled) {
    // برای مدیر، دلیل فنی را هم نشان می‌دهیم تا لازم نباشد لاگ سرور را بگردد.
    const reasons = Object.entries(status.errors ?? {}) as [string, string][]
    return (
      <PanelShell title="واریز و برداشت رمزارز" subtitle="انتقال زنجیره‌ای دارایی‌ها">
        <Card>
          <Empty text="سرویس زنجیره‌ای در حال حاضر فعال نیست. با پشتیبانی تماس بگیرید."
            icon={<AlertTriangle className="h-6 w-6" />} />
          {user?.isAdmin && reasons.length > 0 && (
            <div className="mt-4 rounded-xl border border-down/25 bg-down/[.06] p-3">
              <div className="mb-2 text-[11px] font-bold text-down">
                دلیل (فقط برای مدیر نمایش داده می‌شود):
              </div>
              <div className="space-y-1.5">
                {reasons.map(([net, msg]) => (
                  <div key={net} className="text-[11px] leading-6 text-slate-300">
                    <span className="num ml-2 text-slate-500">
                      {net === '*' ? 'کل بخش زنجیره‌ای' : net}:
                    </span>
                    {msg}
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      </PanelShell>
    )
  }

  return (
    <PanelShell title="واریز و برداشت رمزارز" subtitle="انتقال واقعی دارایی روی شبکه بلاک‌چین">
      {status && !status.mainnet && (
        <div className="mb-5 flex items-start gap-2 rounded-2xl border border-gold/25 bg-gold/[.07] p-4 text-xs leading-6 text-gold">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          این سامانه روی <b>شبکه آزمایشی (testnet)</b> اجرا می‌شود. دارایی واقعی به این آدرس‌ها ارسال نکنید.
        </div>
      )}

      <Tabs value={tab} onChange={setTab} className="mb-5"
        items={[
          { key: 'deposit', label: <span className="flex items-center gap-1.5"><ArrowDownToLine className="h-4 w-4" />واریز</span> },
          { key: 'withdraw', label: <span className="flex items-center gap-1.5"><ArrowUpFromLine className="h-4 w-4" />برداشت</span> },
          { key: 'book', label: 'دفترچه آدرس' },
        ]} />

      {assets.length === 0 ? <Rows n={5} h="h-16" />
        : tab === 'deposit' ? <DepositPane assets={assets} />
        : tab === 'withdraw' ? <WithdrawPane assets={assets} />
        : <AddressBookPane />}
    </PanelShell>
  )
}
